import { lazy, Suspense } from 'react';
import { Hammer } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { LazyErrorBoundary } from '@/components/layout/LazyErrorBoundary';
import { useAuthStore } from '@/stores/authStore';
import type { Metier } from '@/types';

/**
 * OperationsPage dispatch dynamique par métier de la filiale (CTO-FRONT-002 Jour 6).
 * - beton             → BetonDashboard (brief LB-MODULE A→E)
 * - transport_minier  → TransportDashboard (Phase 2, Royal LT)
 * - telecom_wifi      → TelecomDashboard (Phase 2, Sinergie WeFly)
 * - immobilier        → ImmobilierDashboard (Phase 2, David & Co)
 * - autres métiers    → placeholder "module à venir"
 */
const BetonDashboard      = lazy(() => import('@/components/operations/BetonDashboard').then((m) => ({ default: m.BetonDashboard })));
const TransportDashboard  = lazy(() => import('@/components/operations/TransportDashboard').then((m) => ({ default: m.TransportDashboard })));
const TelecomDashboard    = lazy(() => import('@/components/operations/TelecomDashboard').then((m) => ({ default: m.TelecomDashboard })));
const ImmobilierDashboard = lazy(() => import('@/components/operations/ImmobilierDashboard').then((m) => ({ default: m.ImmobilierDashboard })));
const TextprintDashboard  = lazy(() => import('@/components/operations/TextprintDashboard').then((m) => ({ default: m.TextprintDashboard })));

const METIER_LABELS: Record<Metier, string> = {
  beton:            'Opérations · Béton & Construction',
  transport_minier: 'Opérations · Transport minier',
  telecom_wifi:     'Opérations · Réseau WiFi',
  impression:       'Opérations · Imprimerie & Textile',
  immobilier:       'Opérations · Immobilier',
  generic:          'Opérations',
};

export const OperationsPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const metier  = filiale?.metier ?? 'generic';

  return (
    <>
      <Topbar
        title={METIER_LABELS[metier]}
        subtitle={filiale ? `${filiale.fullName} · ${filiale.pays}` : 'Opérations'}
      />
      <LazyErrorBoundary moduleLabel={METIER_LABELS[metier]}>
        <Suspense fallback={
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>
            Chargement du module métier…
          </div>
        }>
          {metier === 'beton' ? (
            <BetonDashboard />
          ) : metier === 'transport_minier' ? (
            <TransportDashboard />
          ) : metier === 'telecom_wifi' ? (
            <TelecomDashboard />
          ) : metier === 'immobilier' ? (
            <ImmobilierDashboard />
          ) : metier === 'impression' ? (
            <TextprintDashboard />
          ) : (
          <div className="ds-content">
            <div className="ds-panel" style={{ padding: 48, textAlign: 'center' }}>
              <Hammer size={36} style={{ margin: '0 auto 12px', color: 'var(--tx-3)' }} />
              <h2 className="font-d" style={{ fontSize: 18, color: 'var(--tx)', marginBottom: 6 }}>
                Module métier "{metier}" — à venir
              </h2>
              <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                Le dashboard {METIER_LABELS[metier]} sera disponible dans une prochaine version.<br />
                Disponibles : <strong>béton</strong> (Leadbeton), <strong>transport minier</strong> (Royal LT), <strong>telecom WiFi</strong> (Sinergie), <strong>immobilier</strong> (David & Co), <strong>imprimerie</strong> (Textprint).
              </p>
            </div>
          </div>
          )}
        </Suspense>
      </LazyErrorBoundary>
    </>
  );
};
